import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';

const GH_USER = import.meta.env.VITE_GITHUB_USERNAME || 'mesumbinshaukat';

const initialContacts = {
  whatsapp: '+92 322 0275616',
  email: 'mesum@worldoftech.company',
  socials: {
    linkedin: 'https://www.linkedin.com/in/mesum-bin-shaukat/',
    github: 'https://github.com/mesumbinshaukat',
    instagram: 'http://instagram.com/mesum.dev'
  }
};

const initialLiveSites = [
  'https://worldoftech.company',
  'https://envisionxperts.com',
  'https://dresswelltailors.com/',
  'https://jayelango.com/',
  'https://www.ebs.co.uk/',
  'https://www.klorane.com/',
  'https://theutilitykilt.com/',
  'https://nextleveldigitalization.com/',
  'https://envisionxperts.com/',
  'https://exaureascents.com/',
  'https://www.cushiehome.com/',
  'https://techcors.com/',
  'https://ecomwisesolutions.com/',
  'https://inkphasepublishers.com/',
  'https://jayelango.com/',
  'https://imancc.com/',
  'https://glytone.com/',
  'https://kaam-consultants.com/',
  'https://kaidova.com/111tree/',
  'http://inkphasepublishers.com/'
];

const skillsCatalog = [
  { category: 'MERN/MEAN', items: ['React', 'Next.js', 'Node.js', 'Express', 'MongoDB', 'Angular', 'TypeScript', 'Vite', 'Redux/RTK', 'Zustand'] },
  { category: 'Backend Frameworks', items: ['Nest.js', 'ASP.NET MVC', 'Django', 'Flask', 'FastAPI', 'GraphQL', 'tRPC'] },
  { category: 'CMS & Commerce', items: ['WordPress Themes', 'WordPress Plugins', 'Shopify Apps', 'Shopify Themes', 'Webflow', 'Magento 2'] },
  { category: 'AI/Automation', items: ['LangChain', 'OpenAI', 'RAG', 'Vector DBs (Pinecone, FAISS)', 'LLM Fine-tuning', 'N8N Automations'] },
  { category: 'DevOps', items: ['Docker', 'Vercel', 'Netlify', 'GitHub Actions', 'NGINX', 'PM2', 'CI/CD'] },
  { category: 'Data & Scraping', items: ['BeautifulSoup', 'Playwright', 'Puppeteer', 'Scrapy', 'Selenium'] },
  { category: 'Business', items: ['Sales Enablement', 'BD Strategy', 'Client Discovery', 'Solution Architecture'] }
];

const usePortfolioStore = create(
  persist(
    (set, get) => ({
  contacts: initialContacts,
  skillsCatalog,
  testimonials: [
    {
      id: 1,
      author: 'Muhammad Sarim',
      role: 'Lead Full Stack Developer | Social Media & Business Growth Consultant',
      text: 'Mesum Bin Shaukat and I collaborated on a custom software development project, which he completed ahead of schedule. I appreciated his services!'
    },
    {
      id: 2,
      author: 'Zohair Adeel',
      role: '.NET Developer | ASP.NET | C# | SQL | ERP Specialist',
      text: 'I have collaborated with Mesum on SEO projects and find him to be an exceptional SEO specialist with deep knowledge of ranking websites on the first page of search engines. His approach balances on-page and off-page with strong domain understanding.'
    },
    {
      id: 3,
      author: 'Abdul Rafay Khan',
      role: 'Software Engineer | Full Stack Development | Backend Systems | Exploring AI & Data Science',
      text: 'I recently worked with Mesum on a copywriting project and was thoroughly impressed with his skills. He delivered high‑quality, engaging content tailored to my audience while maintaining excellent communication and meeting deadlines.'
    },
    {
      id: 4,
      author: 'Huzaifa Irfan',
      role: 'I Build Websites That Bring Clients In | Helping Coaches & Consultants Turn Their Site Into a 24/7 Sales Machine',
      text: 'Mesum is an outstanding software developer! He helped me build my website with exceptional skill, professionalism, and attention to detail. The process was smooth, and the final result exceeded my expectations.'
    }
  ],
  liveSites: initialLiveSites,
  repos: [],
  pinnedProjectIds: [],
  loadingRepos: false,
  about: {
    headline: 'Full‑Stack Engineer, AI Automations & Commerce Solutions',
    summary:
      'I specialize in developing and designing modern, responsive websites tailored to meet your business needs. From sleek portfolios to fully functional e-commerce platforms, I create websites that not only look great but also deliver seamless user experiences. In addition to web design, I offer SEO services to help your site rank higher, attract more visitors, and drive meaningful engagement. Passionate about every project, I strive to exceed expectations with professional service, timely delivery, and outcomes that inspire and impress.'
  },

  fetchRepos: async () => {
    if (get().loadingRepos || get().repos.length) return;
    set({ loadingRepos: true });
    try {
      const [reposRes] = await Promise.all([
        axios.get(`https://api.github.com/users/${GH_USER}/repos?per_page=100&sort=updated`)
      ]);
      let repos = reposRes.data
        .filter(r => !r.fork)
        .map(r => ({
          id: r.id,
          name: r.name,
          description: r.description,
          url: r.html_url,
          homepage: r.homepage,
          topics: r.topics || [],
          language: r.language,
          stars: r.stargazers_count,
          updatedAt: r.updated_at
        }));
      // Ensure user-provided key projects exist even if GitHub API omits or delays them,
      // and override missing descriptions/topics where needed
      const ensureProjects = [
        {
          name: 'N8N',
          url: 'https://github.com/mesumbinshaukat/N8N',
          description: 'A MERN stack application that parses natural language lead generation requests and converts them into structured JSON schemas. Extracts lead count, business type, required/optional fields, and generates sample dork queries for lead generation.',
          language: 'JavaScript',
          topics: ['MERN','NLP','Leads','Automation']
        },
        {
          name: 'EnvisionSuite',
          url: 'https://github.com/mesumbinshaukat/EnvisionSuite',
          description: 'Unified Laravel + React + Inertia suite combining POS (inspired by NexoPOS) with full accounting/ledger (abivia/ledger). Includes RBAC, multi-shop, inventory, Excel exports, and responsive charts.',
          language: 'PHP',
          topics: ['Laravel','React','Inertia','POS','Accounting','RBAC']
        },
        {
          name: 'Dress-Well-Tailor-Website',
          url: 'https://github.com/mesumbinshaukat/Dress-Well-Tailor-Website',
          description: 'Complete dashboard for tailors with built-in POS and invoicing, lightweight UI built with vanilla CSS.',
          language: 'CSS',
          topics: ['POS','Dashboard','Invoicing']
        },
        {
          name: 'private-coding-assistant',
          url: 'https://github.com/mesumbinshaukat/private-coding-assistant',
          description: 'Production-ready Python-based autonomous AI agent for coding tasks, deployable as a serverless API on Vercel free tier.',
          language: 'Python',
          topics: ['AI','Agents','Serverless','Vercel']
        },
        {
          name: 'private-messaging',
          url: 'https://github.com/mesumbinshaukat/private-messaging',
          description: 'Secure, end-to-end encrypted messaging app with RSA+AES, real-time via Socket.IO, PWA, WebRTC calls, encrypted file sharing, JWT auth, Nx monorepo, tests (Jest/Cypress/Supertest), CI/CD and Docker dev env.',
          language: 'TypeScript',
          topics: ['E2EE','WebRTC','PWA','Socket.IO','Nx','CI/CD','Docker']
        },
        {
          name: 'Charms-Builder-Shopify',
          url: 'https://github.com/mesumbinshaukat/Charms-Builder-Shopify',
          description: 'Pixel‑perfect charm builder like Nomination configurator. Front-end for Shopify storefront and PHP back-end app (XAMPP + ngrok) for admin.',
          language: 'JavaScript',
          topics: ['Shopify','Custom App','Configurator']
        },
        {
          name: 'Local-LLM',
          url: 'https://github.com/mesumbinshaukat/Local-LLM',
          description: 'Fully local, advanced LLM assistant for research, automation, code execution, and self‑improvement. No cloud. Built with llama-cpp-python (GGUF), ChromaDB (RAG), FastAPI, and a PyQt6 desktop app.',
          language: 'Python',
          topics: ['LLM','Local','RAG','FastAPI','PyQt6']
        },
        {
          name: 'nova-stack',
          url: 'https://github.com/mesumbinshaukat/nova-stack',
          description: 'NovaStackJS — open-source, monorepo-style full-stack framework in TS/JS with embedded PHP via WebAssembly. Multi-frontend support, robust backend, versatile DB adapters, and a custom AI chatbot engine.',
          language: 'TypeScript',
          topics: ['Framework','Monorepo','TypeScript','WASM','PHP']
        },
        {
          name: 'Sahulat-Ledger',
          url: 'https://github.com/mesumbinshaukat/Sahulat-Ledger',
          description: 'Cloud-based SaaS for SMEs/freelancers in Pakistan to manage profiles, invoices, payments, expenses, and compliance in one place.',
          language: 'TypeScript',
          topics: ['SaaS','Ledger','Invoices']
        },
        {
          name: 'education-prediction',
          url: 'https://github.com/mesumbinshaukat/education-prediction',
          description: 'Web app using ML to predict student performance and an AI-powered chatbot for educational support.',
          language: 'Python',
          topics: ['ML','Education','Chatbot']
        },
        {
          name: 'SmartDineOS',
          url: 'https://github.com/Huzaifa1509/SmartDineOS',
          description: 'SaaS for restaurants: QR code menus + AI personalized dish recommendations.',
          language: 'JavaScript',
          topics: ['SaaS','QR','AI','Restaurants']
        },
        {
          name: 'ebs',
          url: 'https://github.com/muneebsadiq/ebs',
          description: 'European Braking System UK — private PHP codebase; industry SaaS/website under OSL-3.0 license.',
          language: 'PHP',
          topics: ['Automotive','SaaS','OSL-3.0']
        }
      ];
      ensureProjects.forEach(ep => {
        // If exists, patch missing description/topics/homepage
        const idx = repos.findIndex(r => r.name.toLowerCase() === ep.name.toLowerCase());
        if (idx !== -1) {
          const r = repos[idx];
          repos[idx] = {
            ...r,
            description: r.description || ep.description,
            topics: r.topics && r.topics.length ? r.topics : (ep.topics || []),
            language: r.language || ep.language
          };
          return;
        }
        // Otherwise inject lightweight entry
        if (!repos.some(r => r.name.toLowerCase() === ep.name.toLowerCase())) {
          repos = [{
            id: Date.now() + Math.random(),
            name: ep.name,
            description: ep.description,
            url: ep.url,
            homepage: undefined,
            topics: ep.topics || [],
            language: ep.language,
            stars: 0,
            updatedAt: new Date().toISOString()
          }, ...repos];
        }
      });
      // Auto-pin these by name if nothing pinned yet
      const autoPinNames = ensureProjects.map(p => p.name.toLowerCase());
      const currentPins = get().pinnedProjectIds || [];
      const nameToId = new Map(repos.map(r => [r.name.toLowerCase(), r.id]));
      const autoPins = currentPins.length ? currentPins : autoPinNames.map(n => nameToId.get(n)).filter(Boolean);
      set({ repos, pinnedProjectIds: autoPins });
    } catch (e) {
      console.error('Failed to fetch repos', e);
    } finally {
      set({ loadingRepos: false });
    }
  },

  addProject: (project) => set((s) => ({ repos: [{
    id: Date.now(),
    name: project.name,
    description: project.description,
    url: project.url,
    homepage: project.homepage,
    topics: project.topics || [],
    language: project.language || 'N/A',
    stars: 0,
    updatedAt: new Date().toISOString()
  }, ...s.repos] })),

  addTestimonial: (t) => set((s) => ({ testimonials: [{
    id: Date.now(),
    author: t.author,
    role: t.role,
    text: t.text
  }, ...s.testimonials] })),

  // Pinning projects
  pinProject: (id) => set((s) => ({ pinnedProjectIds: s.pinnedProjectIds.includes(id) ? s.pinnedProjectIds : [id, ...s.pinnedProjectIds] })),
  unpinProject: (id) => set((s) => ({ pinnedProjectIds: s.pinnedProjectIds.filter(x => x !== id) })),
  getPinnedProjects: () => {
    const { repos, pinnedProjectIds } = get();
    const byId = new Map(repos.map(r => [r.id, r]));
    return pinnedProjectIds.map(id => byId.get(id)).filter(Boolean);
  },

  // Content management actions
  updateAbout: (about) => set(() => ({ about })),
  updateContacts: (contacts) => set(() => ({ contacts })),
  addLiveSite: (url) => set((s) => ({ liveSites: [url, ...s.liveSites] })),
  removeLiveSite: (url) => set((s) => ({ liveSites: s.liveSites.filter(u => u !== url) })),
  addSkillCategory: (category, items) => set((s) => ({ skillsCatalog: [{ category, items }, ...s.skillsCatalog] })),
  removeSkillCategory: (category) => set((s) => ({ skillsCatalog: s.skillsCatalog.filter(c => c.category !== category) })),

  // Import/Export helpers
  exportJSON: () => {
    const { contacts, skillsCatalog, testimonials, liveSites, repos, about } = get();
    return JSON.stringify({ contacts, skillsCatalog, testimonials, liveSites, repos, about }, null, 2);
  },
  importJSON: (json) => {
    try {
      const data = typeof json === 'string' ? JSON.parse(json) : json;
      const next = {};
      if (data.contacts) next.contacts = data.contacts;
      if (data.skillsCatalog) next.skillsCatalog = data.skillsCatalog;
      if (data.testimonials) next.testimonials = data.testimonials;
      if (data.liveSites) next.liveSites = data.liveSites;
      if (data.repos) next.repos = data.repos;
      if (data.about) next.about = data.about;
      set(next);
      return true;
    } catch (e) {
      console.error('Failed to import JSON', e);
      return false;
    }
  }
}), {
  name: 'mesum_portfolio_store',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({
    contacts: state.contacts,
    skillsCatalog: state.skillsCatalog,
    testimonials: state.testimonials,
    liveSites: state.liveSites,
    repos: state.repos,
    about: state.about,
    pinnedProjectIds: state.pinnedProjectIds
  })
})
);

export default usePortfolioStore;


